/**
 * build-export-tables — converts serializeDoc() output into the "export IR":
 * the `tables` object the dialect SQL generators in this folder consume (see
 * their header doc comment for the full shape).
 *
 * Why this exists: diagram data lives in a Y.Doc. Relationships are stored only
 * as `lines` (source/target column ids), never on columns, and a few legacy
 * column fields were renamed/reshaped. This module is the single place that
 * knows the serialized doc shape and rebuilds the per-column shape the
 * generators expect — including reconstructing foreign keys from `lines`.
 *
 * FK direction: for each `reference` line the `'many'` endpoint is the child
 * (holds the FK) and the `'one'` endpoint is the referenced parent. `isReversed`
 * is purely visual and is ignored. Only `reference` lines become FKs.
 */

function reshapeColumn(c) {
    const out = { ...c };

    // rename isNotNull -> isNN
    out.isNN = c.isNotNull ?? false;
    delete out.isNotNull;

    // enum values only when explicitly flagged (avoid leaking stale data)
    out.enumValues = c.isEnum ? (c.enumValues ?? null) : null;
    delete out.isEnum;

    // fields the Y.Doc does not model yet — defaulted so generators don't choke
    out.onUpdate = null;
    out.isGeneratedValueVirtual = false;
    out.isGeneratedValueStored = false;

    // FK fields initialised here; populated from lines in applyForeignKeys
    out.isFK = false;
    out.fkRefTable = null;
    out.fkRefColumn = null;

    // FK referential actions live on the relationship line; copied onto the
    // child column in applyForeignKeys. Default null (a non-FK column never
    // has its triggers read by the generators).
    out.onUpdateTrigger = null;
    out.onDeleteTrigger = null;

    // The generators dispatch on index.type ('unique' | 'fullText' | 'spatial'
    // | else regular); serializeDoc already produces { type }. Preserve it.
    out.index = (c.index && c.index.type != null) ? { type: c.index.type } : null;

    return out;
}

function buildTables(elements) {
    const tables = {};
    for (const id in elements) {
        const el = elements[id];
        if (!el || el.type !== 'table') continue;
        const srcCols = (el.value && el.value.columns) || {};
        const columns = {};
        for (const colId in srcCols) {
            columns[colId] = reshapeColumn(srcCols[colId]);
        }
        tables[id] = { ...el, value: { ...el.value, columns } };
    }
    return tables;
}

function applyForeignKeys(tables, elements, lines) {
    for (const lineId in lines) {
        const line = lines[lineId];
        const v = line && line.value;
        if (!v) continue;

        // only reference lines are FKs (future 'dependency' lines are ignored)
        if (v.lineType != null && v.lineType !== 'reference') continue;

        const sourceElementId = v.sourceElementId;
        const targetElementId = v.targetElementId;
        const sourceColumnId = v.fromInsideElementId;
        const targetColumnId = v.toInsideElementId;
        const sourceCard = v.sourceCardinality;
        const targetCard = v.targetCardinality;

        if (!sourceColumnId || !targetColumnId) continue;

        // child holds the FK (the 'many' side); parent is the referenced 'one'.
        let childElId, childColId, parentElId, parentColId;
        if (sourceCard === 'many' && targetCard !== 'many') {
            childElId = sourceElementId; childColId = sourceColumnId;
            parentElId = targetElementId; parentColId = targetColumnId;
        } else if (targetCard === 'many' && sourceCard !== 'many') {
            childElId = targetElementId; childColId = targetColumnId;
            parentElId = sourceElementId; parentColId = sourceColumnId;
        } else {
            // both equal / empty / missing → creation default: source = child
            childElId = sourceElementId; childColId = sourceColumnId;
            parentElId = targetElementId; parentColId = targetColumnId;
        }

        const childTable = tables[childElId];
        const childCol = childTable && childTable.value.columns[childColId];
        const parentEl = elements[parentElId];
        const parentCol = parentEl && parentEl.value && parentEl.value.columns
            ? parentEl.value.columns[parentColId]
            : undefined;
        if (!childCol || !parentEl || parentEl.type !== 'table' || !parentCol) continue;

        childCol.isFK = true;
        childCol.fkRefTable = (parentEl.value.header && parentEl.value.header.name) || null;
        childCol.fkRefColumn = parentCol.name || null;
        childCol.onUpdateTrigger = v.onUpdateTrigger ?? null;
        childCol.onDeleteTrigger = v.onDeleteTrigger ?? null;
    }
}

export function buildExportTables(serialized) {
    const elements = (serialized && serialized.data && serialized.data.elements) || {};
    const lines = (serialized && serialized.data && serialized.data.lines) || {};
    const tables = buildTables(elements);
    applyForeignKeys(tables, elements, lines);
    return tables;
}

export default buildExportTables;
